// frontend/src/pages/TrainingPage.jsx
//
// "Mis cursos": lo que el usuario tiene asignado en su empresa activa.
// La elegibilidad la resuelve el backend; aquí solo se muestra lo que devuelve.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid,
  LinearProgress, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import {
  CheckCircle as DoneIcon,
  MenuBook as CourseIcon,
  PlayArrow as PlayIcon,
  Schedule as ClockIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { api } from '../services/api';

const FILTROS = [
  { value: 'all', label: 'Todos' },
  { value: 'in_progress', label: 'En progreso' },
  { value: 'not_started', label: 'Pendientes' },
  { value: 'completed', label: 'Completados' },
  { value: 'mandatory', label: 'Obligatorios' },
];

const ETIQUETA = {
  not_started: { texto: 'Empezar', color: 'primary' },
  in_progress: { texto: 'Continuar', color: 'primary' },
  completed: { texto: 'Repasar', color: 'success' },
};

const TrainingPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('all');
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(() => {
    setLoading(true);
    setError(null);
    api.training.myCourses()
      .then((res) => setItems(res.data?.items || []))
      .catch((err) => setError(err?.response?.data?.detail || 'No se pudo cargar la capacitación'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const visibles = useMemo(() => {
    let lista = items;
    if (filtro === 'mandatory') lista = lista.filter((c) => c.is_mandatory);
    else if (filtro !== 'all') lista = lista.filter((c) => c.status === filtro);
    const q = busqueda.trim().toLowerCase();
    if (q) {
      lista = lista.filter((c) =>
        (c.title || '').toLowerCase().includes(q) || (c.summary || '').toLowerCase().includes(q));
    }
    return lista;
  }, [items, filtro, busqueda]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <CourseIcon color="primary" />
        <Typography variant="h5" fontWeight={800}>Capacitación</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cursos y recursos asignados a tu cuenta.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Tabs
          value={filtro}
          onChange={(_, v) => setFiltro(v)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ flex: 1, minHeight: 40 }}
        >
          {FILTROS.map((f) => <Tab key={f.value} value={f.value} label={f.label} sx={{ minHeight: 40 }} />)}
        </Tabs>
        <TextField size="small" placeholder="Buscar curso…" value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)} sx={{ minWidth: { sm: 240 } }} />
      </Stack>

      {loading && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}

      {!loading && visibles.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <CourseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={700}>
              {items.length === 0 ? 'Aún no tienes capacitación asignada' : 'Sin cursos con este filtro'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {items.length === 0
                ? 'Cuando Arsenal o tu empresa te asignen contenido, aparecerá aquí.'
                : 'Prueba con otro filtro o limpia la búsqueda.'}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {visibles.map((c) => (
          <Grid item xs={12} sm={6} md={4} key={c.id}>
            <Card variant="outlined" sx={{
              height: '100%', display: 'flex', flexDirection: 'column',
              transition: 'box-shadow .2s', '&:hover': { boxShadow: 3 },
            }}>
              {c.cover_image_url && (
                <Box component="img" src={c.cover_image_url} alt="" loading="lazy"
                  sx={{ width: '100%', height: 140, objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              )}
              <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {c.is_mandatory && <Chip size="small" color="error" label="Obligatorio" sx={{ height: 20 }} />}
                  {c.category && <Chip size="small" variant="outlined" label={c.category} sx={{ height: 20 }} />}
                  {c.status === 'completed' && (
                    <Chip size="small" color="success" icon={<DoneIcon />} label="Completado" sx={{ height: 20 }} />
                  )}
                </Stack>

                <Typography variant="subtitle1" fontWeight={700}>{c.title}</Typography>
                {c.summary && (
                  <Typography variant="body2" color="text.secondary" sx={{
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>{c.summary}</Typography>
                )}

                {c.estimated_minutes ? (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <ClockIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.secondary">
                      {c.estimated_minutes} min
                    </Typography>
                  </Stack>
                ) : null}

                <Box sx={{ mt: 'auto', pt: 1 }}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Progreso</Typography>
                    <Typography variant="caption" fontWeight={700}>{c.percent}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={c.percent}
                    color={c.status === 'completed' ? 'success' : 'primary'}
                    sx={{ height: 6, borderRadius: 3, mb: 1.5 }} />
                  <Button fullWidth variant={c.status === 'completed' ? 'outlined' : 'contained'}
                    color={ETIQUETA[c.status]?.color || 'primary'}
                    startIcon={<PlayIcon />}
                    onClick={() => navigate(`/training/courses/${c.id}`)}>
                    {ETIQUETA[c.status]?.texto || 'Abrir'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default TrainingPage;
